import { FormEvent, ReactNode, useState } from 'react';

import { useIdentity } from './identity';

const inputCls =
  'h-11 w-full rounded-lg bg-control px-3 text-base text-fg shadow-ctl placeholder:text-fg-faint focus:outline-none focus:ring-2 focus:ring-brand/50 md:h-9 md:text-sm';
const buttonCls =
  'mt-4 flex h-9 w-full items-center justify-center rounded-lg bg-brand px-4 text-sm text-black shadow-ctl transition hover:shadow-ctl-hover active:translate-y-px active:shadow-ctl-press hover:bg-cta-hover-bg hover:text-cta-hover-fg disabled:opacity-50 disabled:hover:bg-brand disabled:hover:text-black disabled:hover:shadow-ctl disabled:active:translate-y-0 disabled:active:shadow-ctl';

/** Full-screen gate, copied from spark-board: a spinner while the session resolves, the sign-in screen until someone signs in. */
export function SignInGate({ children }: { children: ReactNode }) {
  const { me, loading, denied } = useIdentity();
  if (loading && !me) return <Splash />;
  if (denied) return <NoAccess />;
  if (!me) return <SignInScreen />;
  return <>{children}</>;
}

const Spinner = () => <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" aria-hidden />;

/** Brand frame shared by the splash and the sign-in card. */
function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-full items-center justify-center bg-ink px-4 py-16 text-fg">
      <div className="w-full max-w-[380px]">
        <div className="mb-6 flex items-center gap-3">
          <img src="/brand/prisma-logo.png" alt="" className="h-10 w-10 [filter:var(--logo-filter)]" />
          <div>
            <div className="text-base font-semibold text-fg-strong">Prisma</div>
            <div className="text-xs text-fg-faint">Sign in to look after your owners</div>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

function Splash() {
  return (
    <Shell>
      <div className="flex items-center justify-center gap-2 rounded-card bg-surface p-6 text-sm text-fg-muted shadow-e3">
        <Spinner />
        Signing you in…
      </div>
    </Shell>
  );
}

/** Zitadel signed you in, but Prisma does not admit you: not on the Staff list, or deactivated. */
function NoAccess() {
  const { signOut } = useIdentity();
  return (
    <Shell>
      <div className="rounded-card bg-surface p-5 shadow-e3">
        <p className="text-sm leading-relaxed text-fg-muted">
          Your account does not have access to Prisma. Ask a Prisma admin to add you on the Staff page, then sign in again.
        </p>
        <button type="button" className={buttonCls} onClick={signOut}>
          Sign out
        </button>
      </div>
    </Shell>
  );
}

function SignInScreen() {
  const { oidc } = useIdentity();
  return <Shell>{oidc ? <SsoSignIn /> : <EmailSignIn />}</Shell>;
}

/** Zitadel SSO: one button that starts the redirect, or asks Prisma again when only it failed. */
function SsoSignIn() {
  const { loginWithSSO, problem, retry } = useIdentity();
  const [busy, setBusy] = useState(false);
  if (problem === 'session') {
    return (
      <div className="rounded-card bg-surface p-5 shadow-e3">
        <p className="text-sm leading-relaxed text-danger">You are signed in, but Prisma did not answer. Try again in a moment.</p>
        <button type="button" className={buttonCls} onClick={retry}>
          Try again
        </button>
      </div>
    );
  }
  return (
    <div className="rounded-card bg-surface p-5 shadow-e3">
      <p className="text-sm leading-relaxed text-fg-muted">
        Sign in with your company account. Only people a Prisma admin has added can get in.
      </p>
      {problem === 'signin' && <p className="mt-2 text-sm text-danger">The sign-in did not finish. Try again.</p>}
      <button
        type="button"
        className={buttonCls}
        disabled={busy}
        onClick={() => {
          setBusy(true);
          loginWithSSO();
        }}
      >
        {busy ? <Spinner /> : 'Sign in with SSO'}
      </button>
    </div>
  );
}

/** Legacy email sign-in without a password (when no Zitadel issuer is set). */
function EmailSignIn() {
  const { signIn } = useIdentity();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      await signIn(email.trim(), name.trim() || undefined);
    } catch {
      setError('That email did not work. Check it and try again.');
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="rounded-card bg-surface p-5 shadow-e3">
      <label className="mb-1 block text-xs text-fg-muted" htmlFor="signin-email">
        Email
      </label>
      <input id="signin-email" className={inputCls} type="email" autoFocus placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)} />
      <label className="mb-1 mt-3 block text-xs text-fg-muted" htmlFor="signin-name">
        Display name <span className="text-fg-faint">(optional)</span>
      </label>
      <input id="signin-name" className={inputCls} placeholder="How your name shows to the team" value={name} onChange={(e) => setName(e.target.value)} />
      {error ? <div className="mt-3 text-xs text-danger">{error}</div> : null}
      <button type="submit" className={buttonCls} disabled={busy || !email.trim()}>
        {busy ? <Spinner /> : 'Continue'}
      </button>
      <p className="mt-3 text-center text-xs text-fg-faint">No password: this sign-in is for local work only.</p>
    </form>
  );
}

export default SignInGate;
